import React, { useEffect, useState } from 'react';
import {
  BellIcon,
  DeviceMobileIcon,
  KeyIcon,
  LightningBoltIcon,
  ShieldCheckIcon,
  SparklesIcon
} from '@heroicons/react/outline';
import Modal from './Modal';
import {
  formatSunsetDate,
  getMigrationCountdown,
  IS_MIGRATION_POPUP_ENABLED,
  MIGRATION_SESSION_KEY,
  MIGRATION_SUNSET_DATE
} from '../helpers/migration';

const IOS_STORE_URL = process.env.REACT_APP_IOS_APP_URL || '';
const ANDROID_STORE_URL = process.env.REACT_APP_ANDROID_APP_URL || '';

const CountdownUnit = ({ value, label }) => (
  <div className="flex flex-col items-center min-w-[44px]">
    <span className="text-xl font-semibold tabular-nums text-white leading-none">{value}</span>
    <span className="text-[9px] uppercase tracking-wider text-white/70 mt-1">{label}</span>
  </div>
);

const FeaturePill = ({ icon: Icon, title }) => (
  <div className="flex-1 flex flex-col items-center text-center px-1">
    <div className="w-8 h-8 rounded-full bg-[#EEEDFD] flex items-center justify-center">
      <Icon className="w-4 h-4 text-primary" />
    </div>
    <p className="mt-1.5 text-[11px] font-semibold text-secondary leading-tight">{title}</p>
  </div>
);

const AppleIcon = () => (
  <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.8-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z" />
  </svg>
);

const PlayStoreIcon = () => (
  <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M3.609 1.814L13.792 12 3.61 22.186a1.004 1.004 0 0 1-.601-.92V2.734a1.004 1.004 0 0 1 .601-.92zm10.967 10.186l2.857 2.857-8.792 5.107 5.935-7.964zm3.617-2.064l2.585 1.494a1.004 1.004 0 0 1 0 1.74l-2.585 1.494-3.214-3.214 3.214-3.514zM14.576 11.5L5.363 2.287l8.213 9.213z" />
  </svg>
);

const StoreButton = ({ href, icon, label, sublabel, variant }) => {
  const isApple = variant === 'apple';

  const handleClick = () => {
    if (!href) return;
    window.open(href, '_blank', 'noopener,noreferrer');
  };

  return (
    <button
      type="button"
      onClick={handleClick}
      className={`flex-1 flex items-center gap-2.5 rounded-xl px-3 py-2.5 transition-all min-w-0 active:scale-[0.98] ${
        isApple ? 'bg-black text-white shadow-md' : 'bg-[#01875F] text-white shadow-md'
      }`}>
      <div
        className={`flex-shrink-0 w-7 h-7 rounded-md flex items-center justify-center ${
          isApple ? 'bg-white/10' : 'bg-white/15'
        }`}>
        {icon}
      </div>
      <div className="text-left min-w-0">
        <p className="text-[9px] uppercase tracking-wide leading-none opacity-75 truncate">
          {sublabel}
        </p>
        <p className="text-xs font-semibold leading-tight mt-0.5 truncate">{label}</p>
      </div>
    </button>
  );
};

const MigrationPopup = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [countdown, setCountdown] = useState(() => getMigrationCountdown());

  useEffect(() => {
    if (!IS_MIGRATION_POPUP_ENABLED) return;

    const dismissed = sessionStorage.getItem(MIGRATION_SESSION_KEY);
    if (!dismissed) {
      setIsOpen(true);
    }
  }, []);

  useEffect(() => {
    if (!isOpen) return undefined;

    const timer = setInterval(() => {
      setCountdown(getMigrationCountdown());
    }, 1000);

    return () => clearInterval(timer);
  }, [isOpen]);

  const handleContinueWeb = () => {
    sessionStorage.setItem(MIGRATION_SESSION_KEY, 'true');
    setIsOpen(false);
  };

  const pad = (value) => String(value).padStart(2, '0');
  const sunsetLabel = formatSunsetDate(MIGRATION_SUNSET_DATE);

  if (!IS_MIGRATION_POPUP_ENABLED) return null;

  return (
    <Modal
      modal={isOpen}
      bareCustomContent
      customContentClassName="w-full max-w-[420px] rounded-2xl overflow-hidden shadow-2xl"
      customContent={
        <div>
          <div className="bg-gradient-to-br from-primary to-[#3D35B8] px-5 pt-5 pb-4 text-white">
            <div className="inline-flex items-center gap-1.5 rounded-full bg-white/15 px-2.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide">
              <SparklesIcon className="w-3 h-3" />
              Your upgrade is ready
            </div>
            <h2 className="mt-3 text-lg font-bold leading-tight">
              Download the New Vinsyt Staff App
            </h2>
            <p className="mt-1.5 text-xs text-white/85 leading-snug">
              We built a faster, cleaner app for teams like yours. Switch by {sunsetLabel} and keep
              working with a smoother Vinsyt experience.
            </p>
          </div>

          <div className="bg-white px-5 py-4">
            <div className="rounded-xl bg-[#F8F7FF] border border-[#E8E6FC] px-3 py-3">
              {countdown.isExpired ? (
                <div className="text-center">
                  <p className="text-[10px] font-semibold uppercase tracking-wide text-primary">
                    Service ended
                  </p>
                  <p className="mt-0.5 text-xs font-semibold text-secondary">
                    Discontinued on {sunsetLabel} — install Vinsyt Staff below.
                  </p>
                </div>
              ) : (
                <>
                  <p className="text-center text-[10px] font-semibold uppercase tracking-wide text-primary">
                    Time left to upgrade
                  </p>
                  <div className="mt-2 flex items-center justify-center gap-2 rounded-lg bg-primary px-2 py-2">
                    <CountdownUnit value={countdown.days} label="Days" />
                    <span className="text-lg font-light text-white/50 pb-3">:</span>
                    <CountdownUnit value={pad(countdown.hours)} label="Hrs" />
                    <span className="text-lg font-light text-white/50 pb-3">:</span>
                    <CountdownUnit value={pad(countdown.minutes)} label="Min" />
                    <span className="text-lg font-light text-white/50 pb-3">:</span>
                    <CountdownUnit value={pad(countdown.seconds)} label="Sec" />
                  </div>
                </>
              )}
            </div>

            <div className="mt-3 rounded-xl border-2 border-primary/20 bg-gradient-to-r from-[#F8F7FF] to-[#EEEDFD] px-3 py-3">
              <div className="flex items-center gap-2.5">
                <div className="flex-shrink-0 w-9 h-9 rounded-full bg-primary flex items-center justify-center">
                  <KeyIcon className="w-4 h-4 text-white" />
                </div>
                <div className="min-w-0">
                  <p className="text-sm font-bold text-secondary">No new account needed</p>
                  <p className="mt-0.5 text-xs text-secondary leading-snug">
                    Use your existing{' '}
                    <span className="font-semibold text-primary">same email and password</span> — no
                    setup needed. Your customer data stays connected.
                  </p>
                </div>
              </div>
              <div className="mt-2 flex items-center justify-center gap-1.5 rounded-full bg-white px-2.5 py-1 text-[11px] font-semibold text-primary border border-[#E8E6FC]">
                <ShieldCheckIcon className="w-3.5 h-3.5 flex-shrink-0" />
                Same credentials. Same account.
              </div>
            </div>

            <div className="mt-3 flex gap-2">
              <FeaturePill icon={LightningBoltIcon} title="Built faster" />
              <FeaturePill icon={DeviceMobileIcon} title="Better UI" />
              <FeaturePill icon={BellIcon} title="Instant alerts" />
            </div>

            <div className="mt-4">
              <p className="text-xs font-bold text-secondary text-center mb-2">
                Get Vinsyt Staff today
              </p>
              <p className="-mt-1 mb-2 text-[11px] text-tertiary text-center">
                Takes less than 1 minute.
              </p>
              <div className="flex gap-2">
                <StoreButton
                  href={IOS_STORE_URL}
                  variant="apple"
                  icon={<AppleIcon />}
                  sublabel="Download on"
                  label="App Store"
                />
                <StoreButton
                  href={ANDROID_STORE_URL}
                  variant="google"
                  icon={<PlayStoreIcon />}
                  sublabel="Get it on"
                  label="Google Play"
                />
              </div>
            </div>

            <button
              type="button"
              className="mt-3 w-full rounded-xl border border-gray-200 py-2.5 text-xs font-medium text-tertiary active:bg-gray-50"
              onClick={handleContinueWeb}>
              Continue with current version for now
            </button>

            <p className="mt-2.5 text-[11px] text-center text-tertiary">
              Need help?{' '}
              <a className="text-primary font-medium" href="mailto:jp@vinsyt.com">
                jp@vinsyt.com
              </a>
            </p>
          </div>
        </div>
      }
    />
  );
};

export default MigrationPopup;
